import type {
  ScrapeFetchStrategy,
  ScrapeProfilePurpose,
  ScrapeProfileRequest,
} from '../../../types/scraping';
import { SCRAPE_LIMITS, WAIT_UNTIL_OPTIONS } from '../../../types/scraping';
import './ScrapeProfileForm.css';

interface ScrapeProfileFormProps {
  value: ScrapeProfileRequest;
  onChange: (next: ScrapeProfileRequest) => void;
  disabled: boolean;
}

/**
 * The profile editor: the fetch half and the prompt half, side by side.
 *
 * A deliberately plain config form. The operators here are developers, so the spec calls for a config
 * editor and a run button rather than a guided profile builder — the useful thing to add is not
 * hand-holding but the per-field hints explaining what each knob does to a real site.
 */
export const ScrapeProfileForm = ({ value, onChange, disabled }: ScrapeProfileFormProps) => {
  const set = <K extends keyof ScrapeProfileRequest>(
    key: K,
    fieldValue: ScrapeProfileRequest[K]
  ) => onChange({ ...value, [key]: fieldValue });

  /** Clamps a numeric input so the form cannot submit a value the API would reject. */
  const setNumber = (
    key: 'waitMilliseconds' | 'timeoutSeconds' | 'viewportWidth' | 'viewportHeight' | 'maxPages',
    raw: string,
    min: number,
    max: number
  ) => {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      set(key, min);
      return;
    }
    set(key, Math.min(Math.max(parsed, min), max));
  };

  const isSchedule = value.purpose === 'ScheduleExtraction';

  return (
    <div className="scrape-form">
      <fieldset className="scrape-form__group" disabled={disabled}>
        <legend className="scrape-form__legend">Identity</legend>

        <label className="scrape-form__field">
          <span className="scrape-form__label">Name</span>
          <input
            className="scrape-form__input"
            type="text"
            value={value.name}
            maxLength={SCRAPE_LIMITS.maxNameLength}
            placeholder="Tourney Machine — 2026 summer schedule"
            onChange={(e) => set('name', e.target.value)}
          />
        </label>

        <label className="scrape-form__field">
          <span className="scrape-form__label">Notes</span>
          <textarea
            className="scrape-form__textarea scrape-form__textarea--short"
            value={value.description ?? ''}
            placeholder="What this targets, and anything known to be flaky about it."
            onChange={(e) => set('description', e.target.value)}
          />
        </label>
      </fieldset>

      <fieldset className="scrape-form__group" disabled={disabled}>
        <legend className="scrape-form__legend">How to fetch the page</legend>

        <label className="scrape-form__field">
          <span className="scrape-form__label">URL</span>
          <input
            className="scrape-form__input"
            type="url"
            value={value.url}
            placeholder="https://example.com/tournament/12345/schedule"
            onChange={(e) => set('url', e.target.value)}
          />
          <span className="scrape-form__hint">
            Must be a public http(s) page. Private and internal addresses are refused at run time.
          </span>
        </label>

        <label className="scrape-form__field">
          <span className="scrape-form__label">Wait until</span>
          <select
            className="scrape-form__input"
            value={value.waitUntil}
            onChange={(e) =>
              set('waitUntil', e.target.value as ScrapeProfileRequest['waitUntil'])
            }
          >
            {WAIT_UNTIL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="scrape-form__hint">
            {WAIT_UNTIL_OPTIONS.find((o) => o.value === value.waitUntil)?.hint}
          </span>
        </label>

        <span className="scrape-form__field" id="scrape-profile-form-wait-for-selector-set-waitforselec-0">
          <span className="scrape-form__label">Wait for selector</span>
          <input
            className="scrape-form__input"
            type="text"
            value={value.waitForSelector ?? ''}
            maxLength={SCRAPE_LIMITS.maxSelectorLength}
            placeholder=".game-row, table.schedule tbody tr"
            onChange={(e) => set('waitForSelector', e.target.value)}
          />
          <span className="scrape-form__hint">
            Optional. A CSS selector that only exists once the real content has loaded. If it never
            appears the run still completes and says so — often the most useful clue about a new site.
          </span>
        </span>

        <div className="scrape-form__row" role="group" aria-labelledby="scrape-profile-form-wait-for-selector-set-waitforselec-0">
          <label className="scrape-form__field scrape-form__field--narrow">
            <span className="scrape-form__label">Settle delay (ms)</span>
            <input
              className="scrape-form__input"
              type="number"
              min={0}
              max={SCRAPE_LIMITS.maxWaitMilliseconds}
              value={value.waitMilliseconds}
              onChange={(e) =>
                setNumber('waitMilliseconds', e.target.value, 0, SCRAPE_LIMITS.maxWaitMilliseconds)
              }
            />
          </label>

          <label className="scrape-form__field scrape-form__field--narrow">
            <span className="scrape-form__label">Timeout (s)</span>
            <input
              className="scrape-form__input"
              type="number"
              min={SCRAPE_LIMITS.minTimeoutSeconds}
              max={SCRAPE_LIMITS.maxTimeoutSeconds}
              value={value.timeoutSeconds}
              onChange={(e) =>
                setNumber(
                  'timeoutSeconds',
                  e.target.value,
                  SCRAPE_LIMITS.minTimeoutSeconds,
                  SCRAPE_LIMITS.maxTimeoutSeconds
                )
              }
            />
          </label>

          <label className="scrape-form__field scrape-form__field--narrow">
            <span className="scrape-form__label">Viewport width</span>
            <input
              className="scrape-form__input"
              type="number"
              min={SCRAPE_LIMITS.minViewport}
              max={SCRAPE_LIMITS.maxViewport}
              value={value.viewportWidth}
              onChange={(e) =>
                setNumber(
                  'viewportWidth',
                  e.target.value,
                  SCRAPE_LIMITS.minViewport,
                  SCRAPE_LIMITS.maxViewport
                )
              }
            />
          </label>

          <label className="scrape-form__field scrape-form__field--narrow">
            <span className="scrape-form__label">Viewport height</span>
            <input
              className="scrape-form__input"
              type="number"
              min={SCRAPE_LIMITS.minViewport}
              max={SCRAPE_LIMITS.maxViewport}
              value={value.viewportHeight}
              onChange={(e) =>
                setNumber(
                  'viewportHeight',
                  e.target.value,
                  SCRAPE_LIMITS.minViewport,
                  SCRAPE_LIMITS.maxViewport
                )
              }
            />
          </label>
        </div>

        <span className="scrape-form__field" id="scrape-profile-form-user-agent-override-set-useragent--1">
          <span className="scrape-form__label">User agent override</span>
          <input
            className="scrape-form__input"
            type="text"
            value={value.userAgent ?? ''}
            maxLength={SCRAPE_LIMITS.maxUserAgentLength}
            placeholder="Leave blank to present as desktop Chrome"
            onChange={(e) => set('userAgent', e.target.value)}
          />
          <span className="scrape-form__hint">
            Worth changing when a site answers 401/403/429 — that is a bot wall, not a render problem.
          </span>
        </span>

        <div className="scrape-form__row" role="group" aria-labelledby="scrape-profile-form-user-agent-override-set-useragent--1">
          <label className="scrape-form__field">
            <span className="scrape-form__label">Next-page selector</span>
            <input
              className="scrape-form__input"
              type="text"
              value={value.nextPageSelector ?? ''}
              maxLength={SCRAPE_LIMITS.maxSelectorLength}
              placeholder="button.next, a[rel=next]"
              onChange={(e) => set('nextPageSelector', e.target.value)}
            />
            <span className="scrape-form__hint">
              Optional. Clicked between page captures; all pages are combined into one extraction.
            </span>
          </label>

          <label className="scrape-form__field scrape-form__field--narrow">
            <span className="scrape-form__label">Max pages</span>
            <input
              className="scrape-form__input"
              type="number"
              min={SCRAPE_LIMITS.minPages}
              max={SCRAPE_LIMITS.maxPages}
              value={value.maxPages}
              onChange={(e) =>
                setNumber(
                  'maxPages',
                  e.target.value,
                  SCRAPE_LIMITS.minPages,
                  SCRAPE_LIMITS.maxPages
                )
              }
            />
          </label>
        </div>

        <label className="scrape-form__checkbox">
          <input
            type="checkbox"
            checked={value.checkRobots}
            onChange={(e) => set('checkRobots', e.target.checked)}
          />
          <span>
            Check robots.txt
            <span className="scrape-form__hint">
              Recorded on each run for visibility. It is not enforced — a disallowed page still gets
              fetched, because this tool is admin-only and low volume.
            </span>
          </span>
        </label>
      </fieldset>

      <fieldset className="scrape-form__group" disabled={disabled}>
        <legend className="scrape-form__legend">When to use this profile</legend>

        <label className="scrape-form__field">
          <span className="scrape-form__label">Purpose</span>
          <select
            className="scrape-form__input"
            value={value.purpose ?? 'Spike'}
            onChange={(e) =>
              onChange({
                ...value,
                purpose: e.target.value as ScrapeProfilePurpose,
              })
            }
            data-testid="scrape-form-purpose"
          >
            <option value="Spike">Scraping tool only</option>
            <option value="ScheduleExtraction">Reading tournament schedules</option>
          </select>
          <small className="scrape-form__hint">
            A schedule profile's prompt must return the pipe-delimited game rows the
            itinerary importer expects. A scraping-tool prompt returns whatever JSON
            you asked it for, so the two are not interchangeable.
          </small>
        </label>

        <label className="scrape-form__field">
          <span className="scrape-form__label">Host</span>
          <input
            className="scrape-form__input"
            value={value.hostPattern ?? ''}
            placeholder="tourneymachine.com"
            onChange={(e) => onChange({ ...value, hostPattern: e.target.value })}
            data-testid="scrape-form-host-pattern"
          />
          <small className="scrape-form__hint">
            Matched on whole labels, so this also covers www. and other subdomains —
            but never a lookalike like nottourneymachine.com. Leave blank to use this
            profile only as the fallback.
          </small>
        </label>

        <label className="scrape-form__checkbox">
          <input
            type="checkbox"
            checked={value.isDefault ?? false}
            onChange={(e) => onChange({ ...value, isDefault: e.target.checked })}
            data-testid="scrape-form-is-default"
          />
          <span>Use as the fallback when no host matches</span>
        </label>

        <label className="scrape-form__field">
          <span className="scrape-form__label">How to fetch</span>
          <select
            className="scrape-form__input"
            value={value.fetchStrategy ?? 'RenderThenFirecrawl'}
            onChange={(e) =>
              onChange({
                ...value,
                fetchStrategy: e.target.value as ScrapeFetchStrategy,
              })
            }
            data-testid="scrape-form-fetch-strategy"
          >
            <option value="RenderThenFirecrawl">Our renderer, then Firecrawl if it fails</option>
            <option value="RenderOnly">Our renderer only</option>
            <option value="FirecrawlOnly">Firecrawl only</option>
          </select>
          <small className="scrape-form__hint">
            Firecrawl gets past bot protection our renderer is refused by, but each
            read costs a credit and sends the link to Firecrawl. Pick Firecrawl only
            for a site you know is protected, so we don't spend a slow failed render
            first.
          </small>
        </label>

        <label className="scrape-form__field">
          <span className="scrape-form__label">Rewrite the link (pattern)</span>
          <input
            className="scrape-form__input"
            value={value.urlRewritePattern ?? ''}
            placeholder="^(https://tournaments\.prepbaseballreport\.com)/events/([^/?#]+).*$"
            onChange={(e) => onChange({ ...value, urlRewritePattern: e.target.value })}
            data-testid="scrape-form-url-rewrite-pattern"
          />
        </label>

        <label className="scrape-form__field">
          <span className="scrape-form__label">Rewrite the link (replacement)</span>
          <input
            className="scrape-form__input"
            value={value.urlRewriteReplacement ?? ''}
            placeholder="$1/pitch-count/$2"
            onChange={(e) => onChange({ ...value, urlRewriteReplacement: e.target.value })}
            data-testid="scrape-form-url-rewrite-replacement"
          />
          <small className="scrape-form__hint">
            For sites whose shared link isn't the page with the games. A regular
            expression; if it doesn't match, or produces a bad link, the pasted link
            is read as-is.
          </small>
        </label>
      </fieldset>

      <fieldset className="scrape-form__group" disabled={disabled}>
        <legend className="scrape-form__legend">How to interpret the page</legend>

        <label className="scrape-form__field">
          <span className="scrape-form__label">
            {isSchedule ? 'Site context' : 'Extraction prompt'}
          </span>
          <textarea
            className="scrape-form__textarea"
            value={value.extractionPrompt}
            placeholder={
              isSchedule
                ? 'What is unusual about this site? e.g. "The team before VS is home. Day headings like \u201cSaturday - September 12, 2026\u201d date the games below them."'
                : 'Describe the data to pull out and the JSON shape to return it in.'
            }
            onChange={(e) => set('extractionPrompt', e.target.value)}
          />
          <span className="scrape-form__hint">
            {isSchedule
              ? 'Added to the standard schedule prompt, which already defines the game rows the importer reads. Describe only what is specific to this site: how games, teams and dates appear, and which labels are not team names.'
              : 'The output shape is whatever you ask for here \u2014 there is no fixed schema yet. The page text is appended automatically, along with an instruction to reply with JSON only.'}
          </span>
        </label>
      </fieldset>
    </div>
  );
};
